import { Link } from "@tanstack/react-router";
import {
  CANONICAL_PRODUCT_CATEGORIES,
  normalizeProductCategory,
  type ProductCategory,
} from "@the-drop/customer-shared/normalize-product-category";
import { dispensaryStorefrontBannerUrl } from "@the-drop/customer-shared/api/url-builders";
import { DispensaryStorefrontPlaceholder } from "@the-drop/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getCustomerApiBaseUrl } from "../lib/api-base";
import type { PublicMenuLineItem } from "../lib/dispensary-menu-types";
import { FilterPillGroup } from "../components/FilterPillGroup";
import { ProductCardSkeleton } from "../components/ProductCardSkeleton";
import { resolveProductImageUrl } from "../lib/category-placeholder";
import { writeCheckoutShopMeta } from "../lib/checkout-shop-meta";
import {
  DispensaryMenuHttpError,
  DispensaryMenuNotFoundError,
  fetchDispensaryMenu,
} from "../lib/fetch-dispensary-menu";
import { formatCentsAsUsd } from "../lib/format-menu";
import { formatProductSize } from "../lib/format-product-size";
import { useCartStore } from "../store/cart-store";

/**
 * Marketplace-flavoured item: the same `PublicMenuLineItem` shape as a
 * dispensary's menu, plus the dispensary slug + display name needed by
 * the cross-dispensary marketplace card to render a "shop" chip and
 * link out to the per-dispensary menu. The cross-dispensary screen
 * builds these by flattening per-shop fetches; the per-dispensary
 * `MenuScreen` keeps using the bare `PublicMenuLineItem` and the chip
 * doesn't render. See `apps/customer-web/src/pages/MarketplaceScreen.tsx`.
 */
export type MarketplaceItem = PublicMenuLineItem & {
  dispensarySlug: string;
  dispensaryDisplayName: string;
};

function isMarketplaceItem(item: PublicMenuLineItem | MarketplaceItem): item is MarketplaceItem {
  return typeof (item as MarketplaceItem).dispensarySlug === "string";
}

/**
 * Shortened from 60 s so the menu tracks `metrc_packages.quantity_available` more aggressively
 * between orders in concurrent tabs. Combined with `visibilitychange` / `focus` listeners below
 * this shrinks the window for two sessions to race on the last unit of a package.
 */
const MENU_REFETCH_MS = 15_000;

function categorySectionId(category: string): string {
  const slug = category
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `menu-cat-${slug || "other"}`;
}

type Props = { dispensarySlug: string };

const CART_TIP_AUTODISMISS_MS = 3_000;

export function MenuScreen({ dispensarySlug }: Props) {
  const [items, setItems] = useState<PublicMenuLineItem[] | null>(null);
  const [dispensaryLabel, setDispensaryLabel] = useState("");
  /** `storefrontBannerUpdatedAt` from the menu payload — non-null means the
   *  shop uploaded a custom banner; it doubles as the image cache-buster. */
  const [bannerVersion, setBannerVersion] = useState<string | null>(null);
  /** Custom banner failed to load (deleted between refetches, network) —
   *  fall back to the bundled placeholder instead of a broken image. */
  const [bannerBroken, setBannerBroken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** Local-only state — any inbound `?cat=` from the marketplace is ignored.
   *  `dispensaryMenuRoute.validateSearch` doesn't include `cat` so the router
   *  drops it; this initializer makes the intent explicit. */
  const [activeCategory, setActiveCategory] = useState<string | "all">("all");
  /** Live product search within this shop's menu (local-only, like `activeCategory`). */
  const [menuQuery, setMenuQuery] = useState("");
  const [cartTip, setCartTip] = useState<{ kind: "ok" | "warn"; message: string } | null>(null);
  const hasItemsInThisShopCart = useCartStore(
    (s) => (s.carts[dispensarySlug] ?? []).length > 0,
  );
  const addOrIncrement = useCartStore((s) => s.addOrIncrement);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchDispensaryMenu(dispensarySlug);
      const d = data.dispensary;
      if (d && typeof d.id === "string" && typeof d.publicSlug === "string" && d.publicSlug.length > 0) {
        writeCheckoutShopMeta(dispensarySlug, { dispensaryId: d.id, publicSlug: d.publicSlug });
      }
      setItems(data.items);
      setDispensaryLabel(data.dispensary.dbaName?.trim() || data.dispensary.legalName);
      setBannerVersion(data.dispensary.storefrontBannerUpdatedAt ?? null);
      useCartStore.getState().reconcileCartWithMenu(dispensarySlug, data.items);
    } catch (e) {
      if (e instanceof DispensaryMenuNotFoundError) {
        setError(
          "No active shop was found for this URL. The dispensary may need a public slug and active inventory in the API.",
        );
      } else if (e instanceof DispensaryMenuHttpError) {
        if (e.apiHint) {
          setError(e.apiHint);
        } else if (e.status === 500) {
          setError(
            "The API returned HTTP 500 (internal error). Check the API terminal logs. If Postgres reports an undefined column public_slug, run scripts/migrations/011-dispensaries-public-slug.sql against your database.",
          );
        } else {
          setError(
            `The API returned HTTP ${e.status}. Start the API on port 3000 (pnpm --filter @the-drop/api dev) and ensure it can reach Postgres.`,
          );
        }
      } else if (e instanceof TypeError && e.message === "fetch_failed") {
        setError(
          "Could not connect to the API. Tips: (1) Start the API — from repo root run pnpm dev:customer-web (API + Vite), or pnpm --filter @the-drop/api dev in another terminal. (2) If services/api/.env sets PORT, the Vite dev proxy uses that port for /api (not only 3000). (3) If apps/customer-web/.env sets VITE_API_BASE_URL, the browser calls that host directly — remove it to use the proxy, or point it at a running API with CORS allowing this origin.",
        );
      } else if (e instanceof SyntaxError && e.message === "menu_json") {
        setError("The API response was not valid JSON (wrong URL or proxy target?).");
      } else {
        setError("The menu could not be loaded. Check that the API is running and try again.");
      }
      setItems(null);
      setDispensaryLabel("");
    } finally {
      setLoading(false);
    }
  }, [dispensarySlug]);

  /** A new upload gets a fresh cache-busted URL — give it another chance. */
  useEffect(() => {
    setBannerBroken(false);
  }, [bannerVersion]);

  useEffect(() => {
    setLoading(true);
    void load();
    const id = window.setInterval(() => void load(), MENU_REFETCH_MS);

    /**
     * Refetch the moment the tab regains attention — a user coming back from placing an order in
     * another tab, or from a long-idle window, would otherwise see a stale snapshot until the next
     * interval tick.
     */
    function onVisibilityOrFocus() {
      if (document.visibilityState === "visible") {
        void load();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityOrFocus);
    window.addEventListener("focus", onVisibilityOrFocus);

    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibilityOrFocus);
      window.removeEventListener("focus", onVisibilityOrFocus);
    };
  }, [load]);

  /** Canonical categories (Concentrate / Flower / Vapes / Edibles) with at
   *  least one item. Drops empty cats from BOTH the pill row and the All-mode
   *  section render so the user can never click into "Nothing in this
   *  category." */
  /** Items surviving the search box; the category pills and sections all
   *  derive from this so search + category compose predictably. */
  const searchedItems = useMemo<PublicMenuLineItem[] | null>(() => {
    if (!items) return null;
    const q = menuQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      [i.productName, i.productCategory, i.productStrain ?? "", i.brandName ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [items, menuQuery]);

  const nonEmptyCategories = useMemo<ProductCategory[]>(() => {
    if (!searchedItems) return [];
    const present = new Set<ProductCategory>();
    for (const item of searchedItems) {
      present.add(normalizeProductCategory(item.productCategory, item.productName));
    }
    return CANONICAL_PRODUCT_CATEGORIES.filter((c) => present.has(c));
  }, [searchedItems]);

  /** If the URL or stale state references a category that's no longer in the
   *  data (e.g., last item sold out + refetched), fall back to "all" so the
   *  user isn't stuck on an empty filter. */
  useEffect(() => {
    if (
      activeCategory !== "all" &&
      !nonEmptyCategories.includes(activeCategory as ProductCategory)
    ) {
      setActiveCategory("all");
    }
  }, [activeCategory, nonEmptyCategories]);

  const visibleItems = useMemo(() => {
    if (!searchedItems) return [];
    if (activeCategory === "all") return searchedItems;
    return searchedItems.filter(
      (i) => normalizeProductCategory(i.productCategory, i.productName) === activeCategory,
    );
  }, [searchedItems, activeCategory]);

  /** Auto-dismiss matches the marketplace cart-tip pill so the two screens
   *  feel like one product. */
  useEffect(() => {
    if (!cartTip) return;
    const id = window.setTimeout(() => setCartTip(null), CART_TIP_AUTODISMISS_MS);
    return () => window.clearTimeout(id);
  }, [cartTip]);

  function onCategoryNav(cat: string) {
    setActiveCategory(cat);
    if (cat !== "all") {
      document.getElementById(categorySectionId(cat))?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function handleAddToCart(item: PublicMenuLineItem) {
    const r = addOrIncrement(dispensarySlug, item, 1);
    if (r.ok) {
      setCartTip({ kind: "ok", message: "Added to cart." });
    } else {
      setCartTip({ kind: "warn", message: r.reason });
    }
  }

  /** Skeleton on initial load only — `items === null && loading && !error`.
   *  Subsequent 15-s refetches replace data in place so there's no flash. */
  if (loading && items === null && !error) {
    return (
      <div className="mx-auto w-full max-w-[1200px] pt-6">
        <ul
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
          aria-busy="true"
        >
          {Array.from({ length: 8 }, (_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </ul>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[1200px] pt-6">
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-[var(--badge-red)]">{error}</p>
          <button
            type="button"
            className="mt-3 rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--green-dark)]"
            onClick={() => {
              setLoading(true);
              void load();
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
      {/* Storefront banner — the shop's uploaded image, or the bundled
       *  placeholder illustration when none is configured. Fixed-height hero
       *  box; both variants cover-crop to fill it. */}
      <div className="h-40 overflow-hidden rounded-2xl border border-[var(--card-border)] sm:h-52 md:h-60">
        {bannerVersion && !bannerBroken ? (
          <img
            src={dispensaryStorefrontBannerUrl(getCustomerApiBaseUrl(), dispensarySlug, bannerVersion)}
            alt={`${dispensaryLabel || "Dispensary"} storefront banner`}
            className="h-full w-full object-cover"
            onError={() => setBannerBroken(true)}
          />
        ) : (
          <DispensaryStorefrontPlaceholder className="h-full w-full" title="" />
        )}
      </div>

      <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
        <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">
          Shop
        </p>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
            {dispensaryLabel || "Menu"}
          </h1>
          {hasItemsInThisShopCart ? (
            <Link
              to="/$dispensarySlug/cart"
              params={{ dispensarySlug }}
              className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
            >
              View cart →
            </Link>
          ) : null}
        </div>
      </header>

      {cartTip ? (
        <div
          role="status"
          className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium ${
            cartTip.kind === "ok"
              ? "bg-[var(--surface-sage)] text-[var(--green-dark)]"
              : "bg-amber-50 text-amber-900"
          }`}
        >
          {cartTip.message}
        </div>
      ) : null}

      {/* Sticky on tablet+ — solid `bg-[var(--page-bg)]` so the H1 doesn't bleed
       *  through during the scroll transition. Mobile keeps it inline. */}
      <div className="space-y-3 md:sticky md:top-16 md:z-20 md:-mx-4 md:bg-[var(--page-bg)] md:px-4 md:py-3 lg:-mx-7 lg:px-7">
        <label className="block">
          <span className="sr-only">Search this menu</span>
          <input
            type="search"
            value={menuQuery}
            onChange={(e) => setMenuQuery(e.target.value)}
            placeholder="Search this menu"
            autoComplete="off"
            className="h-11 w-full rounded-full border border-[var(--card-border)] bg-white px-5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)] focus:border-[var(--green)] focus:outline-none focus:ring-1 focus:ring-[var(--green)]"
          />
        </label>
        <FilterPillGroup
          label="Category"
          value={activeCategory}
          onChange={onCategoryNav}
          options={[
            { value: "all", label: "All" },
            ...nonEmptyCategories.map((c) => ({ value: c, label: c })),
          ]}
        />
      </div>

      {items && items.length === 0 ? (
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-8 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">
            No products listed for this shop yet.
          </p>
        </div>
      ) : searchedItems && searchedItems.length === 0 ? (
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-8 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">
            No products match &ldquo;{menuQuery.trim()}&rdquo;.
          </p>
          <button
            type="button"
            onClick={() => setMenuQuery("")}
            className="mt-4 rounded-lg border border-[var(--card-border)] bg-white px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--page-bg)]"
          >
            Clear search
          </button>
        </div>
      ) : activeCategory === "all" ? (
        /* All mode is a browse view: one horizontally scrollable shelf row per
         * category (capped) with a "View all" that switches to that category's
         * full grid — keeps every category visible within a short scroll. */
        <div className="space-y-8">
          {nonEmptyCategories.map((cat) => {
            const catItems = searchedItems!.filter(
              (i) => normalizeProductCategory(i.productCategory, i.productName) === cat,
            );
            return (
              <section key={cat} id={categorySectionId(cat)} className="scroll-mt-32">
                <div className="mb-3 flex items-baseline justify-between gap-4">
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">{cat}</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveCategory(cat);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="text-sm font-medium text-[var(--green)] transition-colors hover:text-[var(--green-dark)]"
                  >
                    View all ({catItems.length}) →
                  </button>
                </div>
                <ProductShelfRow items={catItems} onAddToCart={handleAddToCart} />
              </section>
            );
          })}
        </div>
      ) : (
        <ProductGrid items={visibleItems} onAddToCart={handleAddToCart} />
      )}
    </div>
  );
}

/** Shelf rows render at most this many cards; "View all" has the rest. */
const SHELF_ROW_MAX_ITEMS = 12;

/**
 * One horizontally scrollable line of product cards for the All-categories
 * browse view. Cards are fixed-width so the row's height stays constant and
 * the next category section is visible after a short vertical scroll.
 */
function ProductShelfRow({
  items,
  onAddToCart,
}: {
  items: PublicMenuLineItem[];
  onAddToCart: (item: PublicMenuLineItem) => void;
}) {
  return (
    <ul className="-mx-1 flex snap-x gap-4 overflow-x-auto px-1 pb-2">
      {items.slice(0, SHELF_ROW_MAX_ITEMS).map((item) => (
        <ProductCard
          key={item.metrcPackageId}
          item={item}
          onAddToCart={onAddToCart}
          className="w-56 flex-none snap-start sm:w-64"
        />
      ))}
    </ul>
  );
}

export function ProductGrid({
  items,
  onAddToCart,
}: {
  items: PublicMenuLineItem[] | MarketplaceItem[];
  onAddToCart: (item: PublicMenuLineItem) => void;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-[var(--text-muted)]">Nothing in this category.</p>;
  }
  return (
    <ul className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
      {(items as Array<PublicMenuLineItem | MarketplaceItem>).map((item) => (
        <ProductCard key={item.metrcPackageId} item={item} onAddToCart={onAddToCart} />
      ))}
    </ul>
  );
}

function ProductCard({
  item,
  onAddToCart,
  className = "",
}: {
  item: PublicMenuLineItem | MarketplaceItem;
  onAddToCart: (item: PublicMenuLineItem) => void;
  /** Extra layout classes from the container (e.g. fixed width in shelf rows). */
  className?: string;
}) {
  const sizeSubtitle = formatProductSize(item);
  const dimmed = item.soldOut;
  const imgUrl = resolveProductImageUrl(item);
  return (
    <li
      className={`flex flex-col overflow-hidden rounded-2xl border border-[var(--card-border)] bg-white shadow-cardMd transition-opacity ${
        dimmed ? "opacity-60" : ""
      } ${className}`}
    >
      {/* Image area — cascade per `lib/category-placeholder.ts` always returns
       *  a URL (Flower as the unrecognized-category default), so the eyebrow
       *  always renders on a translucent pill against the photo. */}
      <div className="relative flex aspect-[4/3] items-end p-4">
        <img
          src={imgUrl}
          alt=""
          loading="lazy"
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        <span
          className={`relative z-10 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[.1em] text-white backdrop-blur-sm ${
            item.soldOut ? "bg-black/50" : "bg-black/40"
          }`}
        >
          {item.soldOut
            ? "Sold out"
            : normalizeProductCategory(item.productCategory, item.productName)}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="line-clamp-2 text-sm font-semibold text-[var(--text-primary)]">
          {item.productName}
        </p>
        {isMarketplaceItem(item) ? (
          <Link
            to="/$dispensarySlug/menu"
            params={{ dispensarySlug: item.dispensarySlug }}
            className="text-xs text-[var(--text-muted)] transition-colors hover:text-[var(--green)]"
          >
            {item.dispensaryDisplayName}
          </Link>
        ) : null}
        {isMarketplaceItem(item) ? (
          <>
            {/* COMP-001: prices may only be shown once the customer enters a specific dispensary. See docs/compliance-rules.md. */}
            {sizeSubtitle ? (
              <p className="text-xs text-[var(--text-muted)]">{sizeSubtitle}</p>
            ) : null}
            <Link
              to="/$dispensarySlug/menu"
              params={{ dispensarySlug: item.dispensarySlug }}
              className="mt-auto w-full rounded-lg bg-[var(--green)] py-2 text-center text-xs font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              View menu
            </Link>
          </>
        ) : (
          <>
            <p className="text-base font-bold text-[var(--text-primary)]">
              {formatCentsAsUsd(item.unitPriceCents)}
              {sizeSubtitle ? (
                <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">{sizeSubtitle}</span>
              ) : null}
            </p>
            <button
              type="button"
              disabled={item.soldOut}
              className="mt-auto w-full rounded-lg bg-[var(--green)] py-2 text-xs font-semibold text-white transition-colors hover:bg-[var(--green-dark)] disabled:cursor-not-allowed disabled:bg-[var(--green-pale)] disabled:text-[var(--text-muted)]"
              onClick={() => onAddToCart(item)}
            >
              {item.soldOut ? "Sold out" : "Add to cart"}
            </button>
          </>
        )}
      </div>
    </li>
  );
}
